import Export16 from '@carbon/icons-react/es/export/16';
import { Tooltip } from 'antd';
import moment from 'moment';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'components/atoms/button';
import { logger } from 'services';
import { ExportDialog } from '../export-modal';

const PDF_EXPORT_STYLES_ID = 'pdf-export-styles';

const smoothScroll = (targetElem: HTMLElement, speed = 25): Promise<void> => {
  return new Promise((resolve) => {
    const toBeScrolled = targetElem.scrollHeight - targetElem.offsetHeight;

    const scroll = () => {
      const currentScroll = targetElem.scrollTop;
      const distanceToTarget = toBeScrolled - currentScroll;

      if (distanceToTarget < speed) {
        targetElem.scrollBy(0, distanceToTarget);
        resolve();
      } else {
        targetElem.scrollBy(0, speed);
        requestAnimationFrame(scroll);
      }
    };

    scroll();
  });
};

const injectCustomStyles = () => {
  const isAlreadyInjected = !!document.getElementById(PDF_EXPORT_STYLES_ID);

  if (isAlreadyInjected) {
    return;
  }
  /**
   * hardcoding styled components version here, since there is no identifier
   * to select style tag injected by styled components
   */
  const styleSheet = Object.values(document.styleSheets).find(
    // styled-components version has to be updated here whenever the pkg is upgraded
    (_style) => (_style?.ownerNode as any)?.dataset?.styledVersion === '5.3.6',
  );

  if (!styleSheet) {
    logger.error(
      'Unable to get style sheets for pdf export, please check styled components version',
      { error: new Error('PDF_EXPORT_STYLES_MISSING') },
    );

    return;
  }
  const styleString = Array.from(styleSheet.cssRules)
    .map((rule) => rule.cssText)
    .join(' ');

  const styleTag = document.createElement('style');

  styleTag.id = PDF_EXPORT_STYLES_ID;

  styleTag.innerHTML = styleString;
  document.head.appendChild(styleTag);
};

const removeCustomStyles = () => {
  document.getElementById(PDF_EXPORT_STYLES_ID)?.remove();
};

export const ExportTimelineButton: FC<{
  onClick: VoidFunction;
}> = ({ onClick }) => (
  <Tooltip title={<FormattedMessage id="export" />}>
    <Button icon={<Export16 />} type="text" onClick={onClick} />
  </Tooltip>
);

export const ExportTimelineDialog: FC<{
  projectName: string;
  filter;
  filters;
  ganttRef;
  onCancel;
  onSettingsChange;
}> = ({
  projectName,
  filter,
  filters,
  ganttRef,
  onCancel,
  onSettingsChange,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handlePrint = async (formValues) => {
    const gantt = ganttRef?.ganttInstance;
    const timestamp = moment().format('DD-MMM-YYYY_h-mma');
    const filename = `${projectName}_${timestamp}`;

    switch (formValues.exportFormat) {
      case 'excel':
        gantt.features.excelExporter.export({
          filename,
        });
        break;

      case 'pdf':
        setIsExporting(true);
        /**
         * Gantt uses virtualisation due to which when larger projects are exported as pdf
         * the task name will be blank since custom React component is used
         * hence we explicitly scroll to instantiate rendering of all React components.
         */
        await smoothScroll(gantt.byRef.bodyContainer);
        gantt.byRef.bodyContainer.scrollTop = 0;

        /**
         * Workaround to skip buffer period of 1 month added to project start date
         * since `scheduleRange` config of pdf export is throwing exception.
         */
        // gantt.project.setStartDate(
        //   moment(gantt.project.startDate).add(1, 'month').format(),
        // );

        /**
         * styled components injects styles in different way in production mode
         * Ref: https://nocode-nobug.com/things-to-know-about-styled-components/#how-styles-land-in-the-dom
         * hence custom styles are missing in exported pdf in non dev ENV
         * Patch: reading styled component selectors from HEAD and injecting back into HEAD
         * TODO: to come up with an elegant way to handle this.
         */
        injectCustomStyles();

        try {
          await gantt.features.pdfExport.export({
            fileName: filename,
            orientation: formValues.orientation,
            exporterType: 'multipagevertical',
          });
        } catch (e) {
          logger.error('Error caught while exporting timeline as pdf', {
            error: e,
          });
        } finally {
          // gantt.project.setStartDate(
          //   moment(gantt.project.startDate).subtract(1, 'month').format(),
          // );
          setIsExporting(false);
          removeCustomStyles();
        }
        break;

      default:
        logger.warn(
          `Unknown file format ${formValues.exportFormat} for timeline export`,
        );
    }
  };

  return (
    <>
      <ExportDialog
        title={<FormattedMessage id="timeline.export.title" />}
        formats={['excel', 'pdf']}
        defaultFormat="excel"
        isLoading={isExporting}
        visible
        selected={filter}
        statusFilters={filters.primary}
        responsibleFilters={filters.secondary}
        onOkay={handlePrint}
        onChange={onSettingsChange}
        onCancel={onCancel}
      />
    </>
  );
};
